import type { FirestoreError } from 'firebase/firestore'

import type { Goal, NewGoalInput } from '@study-decks/shared'

import { getFirebaseAuth } from './firebase.js'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? ''

export class GoalApiError extends Error {
  override readonly name = 'GoalApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

function errorCode(err: unknown): string {
  const code = (err as Partial<FirestoreError>).code
  if (code === 'permission-denied') return 'PERMISSION_DENIED'
  if (code === 'invalid-argument' || code === 'failed-precondition') return 'INVALID_ARGUMENT'
  if (code === 'unavailable') return 'UNAVAILABLE'
  return 'REQUEST_FAILED'
}

function errorMessage(code: string): string {
  if (code === 'PERMISSION_DENIED') return "You don't have access to these goals."
  if (code === 'INVALID_ARGUMENT') return 'Check the goal details and try again.'
  if (code === 'UNAVAILABLE') return "Couldn't reach the database — check your connection."
  return 'Something went wrong — please try again.'
}

function normalizeError(err: unknown): GoalApiError {
  if (err instanceof GoalApiError) return err
  const code = errorCode(err)
  return new GoalApiError(code, errorMessage(code))
}

async function idToken(): Promise<string> {
  const token = await getFirebaseAuth().currentUser?.getIdToken()
  if (!token) throw new GoalApiError('UNAUTHENTICATED', 'Sign in again to manage goals.')
  return token
}

async function requestGoals<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await idToken()
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${token}`,
      ...init.headers,
    },
  })
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string }
    }
    throw new GoalApiError(
      errorBody.error?.code ?? 'REQUEST_FAILED',
      errorBody.error?.message ?? `Goal request failed (${response.status})`,
    )
  }
  return (await response.json()) as T
}

function validateGoalInput(input: NewGoalInput) {
  if (input.targetValue <= 0) {
    throw new GoalApiError('INVALID_ARGUMENT', errorMessage('INVALID_ARGUMENT'))
  }
  if (input.targetMetric === 'accuracy' && input.targetValue > 1) {
    throw new GoalApiError('INVALID_ARGUMENT', 'Accuracy goals must be between 0 and 1.')
  }
  if (input.targetMetric === 'completions' && input.targetValue > 1000) {
    throw new GoalApiError('INVALID_ARGUMENT', 'Completion goals cannot exceed 1000.')
  }
  if (input.targetMetric === 'minutesStudied' && input.targetValue > 600) {
    throw new GoalApiError('INVALID_ARGUMENT', 'Minute goals cannot exceed 600.')
  }
}

export async function listGoalsForClass(classId: string): Promise<Goal[]> {
  try {
    const response = await requestGoals<{ goals: Goal[] }>(
      `/api/goals/classes/${encodeURIComponent(classId)}/goals`,
    )
    return response.goals
  } catch (err) {
    throw normalizeError(err)
  }
}

export async function listActiveGoalsForClasses(
  classIds: string[],
): Promise<Array<Goal & { classId: string }>> {
  const now = new Date().toISOString()
  const results = await Promise.all(
    classIds.map(async (classId) => {
      try {
        return (await listGoalsForClass(classId))
          .filter((goal) => goal.dueAt >= now)
          .map((goal) => ({ ...goal, classId }))
      } catch (err) {
        console.warn(`Could not load goals for class ${classId}:`, err)
        return []
      }
    }),
  )
  return results.flat().sort((a, b) => a.dueAt.localeCompare(b.dueAt))
}

export async function createGoal(classId: string, input: NewGoalInput): Promise<Goal> {
  validateGoalInput(input)
  try {
    const response = await requestGoals<{ goal: Goal }>(
      `/api/goals/classes/${encodeURIComponent(classId)}/goals`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    )
    return response.goal
  } catch (err) {
    throw normalizeError(err)
  }
}

export async function deleteGoal(classId: string, goalId: string): Promise<void> {
  try {
    await requestGoals<{ deleted: boolean }>(
      `/api/goals/classes/${encodeURIComponent(classId)}/goals/${encodeURIComponent(goalId)}`,
      { method: 'DELETE' },
    )
  } catch (err) {
    throw normalizeError(err)
  }
}
